{/* Copyright 2025 Adobe. All rights reserved.
This file is licensed to you under the Apache License, Version 2.0 (the "License");
you may not use this file except in compliance with the License. You may obtain a copy
of the License at http://www.apache.org/licenses/LICENSE-2.0
Unless required by applicable law or agreed to in writing, software distributed under
the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
OF ANY KIND, either express or implied. See the License for the specific language
governing permissions and limitations under the License. */}

import {Layout} from '../../src/Layout';
export default Layout;
import docs from 'docs:@react-aria/label';
import {FunctionAPI} from '../../src/FunctionAPI';
import {InterfaceType} from '../../src/types';

export const section = 'Utilities';
export const description = 'Provides accessibility and labeling support for form field components.';

# useField

<PageDescription>{docs.exports.useField.description}</PageDescription>

## Introduction

The `useField` hook associates a form control with a label, and an optional description and/or error message. This is useful for providing context about how users should fill in a field, or a validation message. `useField` takes care of creating ids for each element and associating them with the correct ARIA attributes (`aria-labelledby` and `aria-describedby`).

By default, `useField` assumes that the label is a native HTML `<label>` element. However, if you are labeling a non-native form element, be sure to use an element other than a `<label>` and set the `labelElementType` prop appropriately.

**Note**: Many other React Aria hooks such as [useTextField](TextField/useTextField.html), [useSelect](Select/useSelect.html), and [useComboBox](ComboBox/useComboBox.html) already include support for description and error message elements. If you're using one of those hooks, there's no need to use `useField`.

## Example

```tsx render
'use client';
import {useField} from 'react-aria';

function ContactPicker(props) {
  let {labelProps, fieldProps, descriptionProps, errorMessageProps} = useField(props);

  return (
    <div style={{display: 'flex', flexDirection: 'column', width: 200, marginBottom: 20}}>
      <label {...labelProps}>{props.label}</label>
      <select {...fieldProps}>
        <option>Email</option>
        <option>Phone</option>
        <option>Fax</option>
        <option>Carrier pigeon</option>
      </select>
      {props.description &&
        <div {...descriptionProps} style={{fontSize: 12}}>{props.description}</div>
      }
      {props.errorMessage &&
        <div {...errorMessageProps} style={{color: '#b00020', fontSize: 12}}>{props.errorMessage}</div>
      }
    </div>
  );
}

<>
  <ContactPicker
    label="Preferred contact method"
    description="Select the best way to contact you about issues with your account." />
  <ContactPicker
    label="Preferred contact method"
    errorMessage="Select a contact method." />
</>
```

## API

<FunctionAPI function={docs.exports.useField} links={docs.links} />

### AriaFieldProps

<InterfaceType {...docs.exports.AriaFieldProps} />

### FieldAria

<InterfaceType {...docs.exports.FieldAria} />
